import type { NestedDeal } from "@revelate/types";
import { getGrowthEfficiencyProperties } from "../state-of-sales-v2/growthEfficiencyData";
import type { CreationExploration } from "./creationExploration";

export type CreationMarketField = { name: string; options: Readonly<Record<string, string>> };
export type CreationDimensionData = { companyId: number; ownerTeams: Readonly<Record<string, string>>; market?: CreationMarketField };

export function existingMarketField(properties: unknown): CreationMarketField | undefined {
  if (!Array.isArray(properties)) return undefined;
  // Market means an actual Market field, not Marketing, country or an inferred
  // industry. Multiple ambiguous matches do not silently choose a definition.
  const matches = properties.filter(row => row && typeof row === "object" && typeof row.name === "string"
    && [row.name, row.label].some(value => typeof value === "string" && ["market", "marknad"].includes(value.trim().toLowerCase()))
    && ["string", "enumeration"].includes(row.type) && row.fieldType !== "checkbox");
  if (matches.length !== 1) return undefined;
  const row = matches[0];
  return { name: row.name, options: Object.fromEntries((Array.isArray(row.options) ? row.options : []).flatMap((option: { value?: unknown; label?: unknown }) =>
    typeof option.value === "string" && typeof option.label === "string" ? [[option.value, option.label]] : [])) };
}

export function primaryOwnerTeams(owners: unknown): Record<string, string> {
  if (!Array.isArray(owners)) return {};
  const result: Record<string, string> = {};
  for (const owner of owners) {
    if (!owner || typeof owner !== "object" || !owner.id || !Array.isArray(owner.teams)) continue;
    const teams = owner.teams.filter((team: { name?: unknown }) => typeof team?.name === "string" && team.name.trim());
    const primary = teams.filter((team: { primary?: boolean }) => team.primary === true);
    const selected = primary.length === 1 ? primary[0] : primary.length === 0 && teams.length === 1 ? teams[0] : undefined;
    if (selected) result[String(owner.id)] = selected.name.trim();
  }
  return result;
}

export async function readCreationDimensions(companyId: number, read: (path: string, cache?: boolean) => Promise<unknown>, readTeams: boolean): Promise<CreationDimensionData> {
  const [properties, owners] = await Promise.all([
    read(`providers/hubspot/deals/properties/${companyId}`),
    readTeams ? read(`providers/hubspot/owners/${companyId}`) : Promise.resolve([]),
  ]);
  if (!Array.isArray(properties)) throw new Error("CRM dimensions could not be read.");
  return { companyId, ownerTeams: primaryOwnerTeams(owners), market: existingMarketField(properties) };
}

export function withCreationDimensions(source: CreationExploration, companyId: number | undefined, rawDeals: NestedDeal[], metadata: CreationDimensionData | undefined): CreationExploration {
  if (!metadata || metadata.companyId !== companyId) return source;
  const byId = new Map(rawDeals.map(deal => [String(deal.id), deal]));
  return { ...source, observations: source.observations.map(row => {
    const raw = byId.get(row.id);
    if (!raw) return row;
    const properties = getGrowthEfficiencyProperties({ ...raw, emails: undefined, meetings: undefined, activity_sync_status: undefined });
    const team = row.dimensions.team && row.dimensions.team !== "Not specified" ? row.dimensions.team : metadata.ownerTeams[String(properties.hubspot_owner_id)];
    const value = metadata.market ? properties[metadata.market.name] : undefined;
    const market = row.dimensions.market && row.dimensions.market !== "Not specified" ? row.dimensions.market
      : typeof value === "string" && value.trim() ? metadata.market?.options[value] ?? value.trim() : undefined;
    return { ...row, dimensions: { ...row.dimensions, team, market } };
  }) };
}
